import { useState, useEffect } from "react";
import { fetchHomepageMetrics } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Homepage metrics grid.
 *
 * Fires once on mount. Cancels the in-flight request automatically if the
 * component unmounts before the response arrives (AbortController cleanup).
 *
 * All seven-day comparison values are preserved as null when the backend
 * returns null (no data in window) — callers should render "—" rather than
 * "0" for null averages.
 *
 * weeklyProgressPct.current is a weighted average: count metrics (units, sqft)
 * are weighted 2x higher than maintenance percentages (ontime, quality) to
 * reflect their higher business priority.
 *
 * @returns {{
 * buildings: { current: string[] },
 * modsOnLine: { current: number, sevenDayAvg: number | null },
 * activePersonnel: { current: number, sevenDayAvg: null },
 * failedJobsToday: { current: number, sevenDayAvg: number | null },
 * avgDailyRollTime: { current: number | null, sevenDayAvg: number | null },
 * weeklyProgressPct: { current: number }, // Weighted average of 4 weekly goals
 * meta: { asOf: string | null },
 * loading: boolean,
 * error: string | null,
 * }}
 */

// ── Module-level cache ────────────────────────────────────────────────────────
// Shared across all HomePage mounts within the same browser session so that a
// quick navigate-away / navigate-back does not re-fetch during the TTL window.
let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 60_000; // 1 minute — homepage metrics refresh on a slow cadence

// ── Safe defaults ─────────────────────────────────────────────────────────────
// Returned during the loading phase so callers never receive undefined fields.
const DEFAULT_STATE = {
  buildings: { current: [] },
  modsOnLine: { current: 0, sevenDayAvg: null },
  activePersonnel: { current: 0, sevenDayAvg: null },
  failedJobsToday: { current: 0, sevenDayAvg: null },
  avgDailyRollTime: { current: null, sevenDayAvg: null },
  weeklyProgressPct: { current: 0 },
  meta: { asOf: null },
};

const useHomepageData = () => {
  const [data, setData] = useState(_cache ?? DEFAULT_STATE);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // serve from cache, skip fetch

    const load = async () => {
      try {
        const result = await fetchHomepageMetrics(controller.signal);
        _cache = result;
        _cacheTime = Date.now();
        setData(result);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load homepage metrics.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return { ...data, loading, error };
};

export default useHomepageData;
